import React from 'react'
import { Alert, Spin } from 'antd'
import { UseQueryResult } from '@tanstack/react-query'
import {
  InvoiceDeliveryItemDto,
  InvoiceEmailPreviewDto,
} from '../../../api/dto/invoice-delivery.dto'
import { useGetDeliveryEmailPreviewQuery } from '../../../api/invoice-delivery-api-client'
import { extractError } from '../../../api/extract-error'
import { SendGridEmailFrame } from './SendGridEmailFrame'
import {
  PreviewHeader,
  PreviewHeaderLabel,
  PreviewHeaderValue,
  PreviewState,
} from './EmailPreview.styles'

interface InvoiceEmailPreviewProps {
  companyName: string
  preview: UseQueryResult<InvoiceEmailPreviewDto, Error>
}

/** Envelope (from, to, subject) above the email rendered from its SendGrid template. */
export const InvoiceEmailPreview: React.FC<InvoiceEmailPreviewProps> = ({
  companyName,
  preview,
}) => {
  if (preview.isPending) {
    return (
      <PreviewState role="status">
        <Spin />
        Loading email from SendGrid…
      </PreviewState>
    )
  }
  if (preview.isError) {
    return (
      <Alert
        type="error"
        showIcon
        message="The email preview could not load"
        description={extractError(preview.error, 'Try again shortly.')}
      />
    )
  }
  const { subject, html } = preview.data
  return (
    <>
      <PreviewHeader>
        <PreviewHeaderLabel>From</PreviewHeaderLabel>
        <PreviewHeaderValue>SureCo Customer Experience</PreviewHeaderValue>
        <PreviewHeaderLabel>To</PreviewHeaderLabel>
        <PreviewHeaderValue>{companyName} billing contacts</PreviewHeaderValue>
        <PreviewHeaderLabel>Subject</PreviewHeaderLabel>
        <PreviewHeaderValue $isSubject>{subject}</PreviewHeaderValue>
      </PreviewHeader>
      <SendGridEmailFrame html={html} title={`Email preview: ${subject}`} />
    </>
  )
}

interface EmailPreviewCardProps {
  delivery: InvoiceDeliveryItemDto
}

/** The email this invoice would send now. */
const EmailPreviewCard: React.FC<EmailPreviewCardProps> = ({ delivery }) => {
  const preview = useGetDeliveryEmailPreviewQuery(delivery.id)
  return (
    <InvoiceEmailPreview
      companyName={delivery.clientName ?? 'Unmatched company'}
      preview={preview}
    />
  )
}

export { EmailPreviewCard }
